'use client'

import React, { useEffect, useState } from 'react'
import { useParams } from 'next/navigation'
import Link from 'next/link'
import RecipeDetails from '@/components/recipe/RecipeDetails'
import RecipePreview from '@/components/preview/RecipePreview'
import { useAppSelector } from 'features/hooks'
import { RootState } from 'features/store'
import { isUserAvailable } from 'helpers/authUser'
import { useGetRecipeLazyQuery } from 'graphql/generated/graphql'
import { Spinner } from 'flowbite-react'

interface RecipePageClientProps {
  initialRecipe?: any
}

const RecipePageClient = ({ initialRecipe }: RecipePageClientProps) => {
  const params = useParams()
  const slug = params?.slug as string | undefined
  const user = useAppSelector((state: RootState) => state.culineerAuth.user)
  const isAuthenticated = isUserAvailable(user)
  const [recipe, setRecipe] = useState<any>(initialRecipe ?? null)
  const [loading, setLoading] = useState(!initialRecipe)
  const [error, setError] = useState<string | null>(null)
  const [getRecipe] = useGetRecipeLazyQuery({ fetchPolicy: 'no-cache' })

  useEffect(() => {
    if (!slug || typeof slug !== 'string') {
      setLoading(false)
      if (!slug) {
        setError('Recipe slug is required')
      }
      return
    }

    const trimmedSlug = slug.trim()
    if (!trimmedSlug) {
      setError('Invalid recipe slug')
      setLoading(false)
      return
    }

    // Server already fetched this recipe for the current slug — no need to refetch.
    if (initialRecipe && initialRecipe.slug === trimmedSlug) {
      return
    }

    const fetchRecipe = async () => {
      try {
        setLoading(true)
        setError(null)

        const res = await getRecipe({
          variables: {
            slug: trimmedSlug,
          },
        })

        if (res.data?.recipe) {
          setRecipe(res.data.recipe)
        } else {
          setError('Recipe not found')
        }
      } catch (err: any) {
        console.error('Error fetching recipe:', err)
        if (err.graphQLErrors && err.graphQLErrors.length > 0) {
          const gqlError = err.graphQLErrors[0]
          if (gqlError.extensions?.code === 'GRAPHQL_VALIDATION_FAILED') {
            setError('Invalid recipe identifier. Please check the URL.')
          } else {
            setError(gqlError.message || 'Failed to load recipe')
          }
        } else if (err.networkError) {
          setError('Network error. Please try again.')
        } else {
          setError(err.message || 'Failed to load recipe')
        }
      } finally {
        setLoading(false)
      }
    }

    fetchRecipe()
  }, [slug, getRecipe, initialRecipe])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner aria-label="loading" size="xl" color={'success'} />
      </div>
    )
  }

  if (error || !recipe) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <div className="text-xl font-bold text-darkGray">
          {error || 'Recipe not found'}
        </div>
        <Link
          href="/recipes"
          className="mt-4 text-primary hover:underline"
        >
          Go back to recipes
        </Link>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <RecipePreview recipe={recipe} />
  }

  return <RecipeDetails slug={slug as string} />
}

export default RecipePageClient
